import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Input from '../../../../components/Input';
import Dropdown from '../../../../components/Dropdown';
import TextArea from '../../../../components/TextArea';
import Switch from '../../../../components/Switch';
import { AddEdit, PropertyContactDetail, PropertyUnitType } from '../../../../interfaces/property.interface';
import { VALIDATION_FIELD_MAPPING } from '../../../../utils/appConstant';
import PropertyContactDetailsTable from './PropertyContactDetailsTable';
import InventoryDetailsTable from './InventoryDetailsTable';
import DatePicker from '../../../../components/DatePicker/index';
import PropertyMap from './PropertyMap';
import { parseDateToLocal } from '../../../../utils/methods';

// Placeholder for CKEditor
import RichTextEditor from '../../../../components/RichTextEditor';


interface Props {
  formData: AddEdit;
  handleChange: (e: any, name: string) => void;
  handlePropertiesContactChange: (data: PropertyContactDetail[]) => void;
  handleInventoryChange: (data: PropertyUnitType[]) => void;
  errors: any;
  view: boolean;
  onInputBlur: (e: any, name: string) => void;
  onKeyDown: (e: any, name?: string) => void;
  
  // Lookups
  categories: any[];
  allCategories: any[];
  constructionStatus: any[];
  allConstructionStatus: any[];
  developers: any[];
  allDevelopers: any[];
  countries: any[];
  allCountries: any[];
  states: any[];
  allStates: any[];
  cities: any[];
  allCities: any[];
  propertyTypes: any[];
  allPropertyTypes: any[];
  bhkTypes: any[];
  allBhkTypes: any[];
  measurements: any[];
  allMeasurements: any[];
  amenities: any[];
  allAmenities: any[];
  areas: any[];
  nearbyAreas: any[];
  allAreas: any[];
  allNearbyAreas: any[];
  approvalStatus: any[];
  allApprovalStatus: any[];

  // Dropdown Search Callbacks
  onDropdownSearchChange: (e: any, name: string) => void;
  dropdownSearchData: any;
}

const PropertyDetailsTab: React.FC<Props> = ({
  formData,
  handleChange,
  handlePropertiesContactChange,
  handleInventoryChange,
  errors,
  view,
  onInputBlur,
  onKeyDown,
  categories,
  allCategories,
  constructionStatus,
  allConstructionStatus,
  developers,
  allDevelopers,
  countries,
  allCountries,
  states,
  allStates,
  cities,
  allCities,
  propertyTypes,
  allPropertyTypes,
  bhkTypes,
  allBhkTypes,
  measurements,
  allMeasurements,
  amenities,
  allAmenities,
  areas,
  nearbyAreas,
  allAreas,
  allNearbyAreas,
  approvalStatus,
  allApprovalStatus,
  onDropdownSearchChange,
  dropdownSearchData,
}) => {
  const { t } = useTranslation();

  /* Options Memos */
  const handleLocalChange = (e: any, name: string) => {
    if (name === VALIDATION_FIELD_MAPPING.latitude || name === VALIDATION_FIELD_MAPPING.longitude) {
        const val = e.target ? e.target.value : e;
        if (typeof val === 'string' && val.includes(',')) {
            const parts = val.split(',').map((s: string) => s.trim());
            // Basic number check on parts
            if (parts.length >= 2 && !isNaN(parseFloat(parts[0])) && !isNaN(parseFloat(parts[1]))) {
                const lat = parts[0];
                const lng = parts[1];
                
                // Update Latitude
                handleChange({ target: { value: lat } }, VALIDATION_FIELD_MAPPING.latitude);
                // Update Longitude
                handleChange({ target: { value: lng } }, VALIDATION_FIELD_MAPPING.longitude);
                return;
            }
        }
        // If not splitting, enforce numeric characters only (digits, dot, minus)
        if (typeof val === 'string') {
             if (!/^[0-9.-]*$/.test(val)) {
                 return; // Prevent update if invalid character is typed
             }
        }
    }
    handleChange(e, name);
  };

  const categoryOptions = useMemo(() => (categories || []).map((item: any) => ({ label: item.value || item.name, value: String(item.id) })), [categories]);
  const allCategoryOptions = useMemo(() => allCategories?.map((item: any) => ({ label: item.value || item.name, value: String(item.id) })), [allCategories]);

  const constructionStatusOptions = useMemo(() => (constructionStatus || []).map((item: any) => ({ label: item.value || item.name, value: String(item.id) })), [constructionStatus]);
  const allConstructionStatusOptions = useMemo(() => allConstructionStatus?.map((item: any) => ({ label: item.value || item.name, value: String(item.id) })), [allConstructionStatus]);

  const developerOptions = useMemo(() => (developers || []).map((item: any) => ({ label: item.name, value: String(item.id) })), [developers]);
  const allDeveloperOptions = useMemo(() => allDevelopers?.map((item: any) => ({ label: item.name, value: String(item.id) })), [allDevelopers]);

  const countryOptions = useMemo(() => (countries || []).map((item: any) => ({ label: item.name, value: String(item.id) })), [countries]);
  const allCountryOptions = useMemo(() => allCountries?.map((item: any) => ({ label: item.name, value: String(item.id) })), [allCountries]);

  const stateOptions = useMemo(() => (states || []).map((item: any) => ({ label: item.name, value: String(item.id) })), [states]);
  const allStateOptions = useMemo(() => allStates?.map((item: any) => ({ label: item.name, value: String(item.id) })), [allStates]);

  const cityOptions = useMemo(() => (cities || []).map((item: any) => ({ label: item.name, value: String(item.id) })), [cities]);
  const allCityOptions = useMemo(() => allCities?.map((item: any) => ({ label: item.name, value: String(item.id) })), [allCities]);

  const amenityOptions = useMemo(() => (amenities || []).map((item: any) => ({ label: item.value || item.name, value: String(item.id) })), [amenities]);
  const allAmenityOptions = useMemo(() => allAmenities?.map((item: any) => ({ label: item.value || item.name, value: String(item.id) })), [allAmenities]);

  const areaOptions = useMemo(() => (areas || []).map((item: any) => ({ label: item.name, value: String(item.id) })), [areas]);
  const allAreaOptions = useMemo(() => allAreas?.map((item: any) => ({ label: item.name, value: String(item.id) })), [allAreas]);

  const nearbyAreaOptions = useMemo(() => (nearbyAreas || []).map((item: any) => ({ label: item.name, value: String(item.id) })), [nearbyAreas]);
  const allNearbyAreaOptions = useMemo(() => allNearbyAreas?.map((item: any) => ({ label: item.name, value: String(item.id) })), [allNearbyAreas]);

  const approvalStatusOptions = useMemo(() => (approvalStatus || []).map((item: any) => ({ label: item.value || item.name, value: String(item.id) })), [approvalStatus]);
  const allApprovalStatusOptions = useMemo(() => allApprovalStatus?.map((item: any) => ({ label: item.value || item.name, value: String(item.id) })), [allApprovalStatus]);


  return (
    <div>
      {/* 1. Basic Details */}
    <div className="card-body">
      {/* 1. Basic Details */}
      <h6 className="mb-0">1. {t('properties.addEdit.pageTexts.form.sections.basicDetails')}</h6>
      <div className="row g-6 mt-1">
             {/* Name */}
            <div className="col-md-6">
              <div className="form-floating form-floating-outline">
                <Input
                  label={t('properties.addEdit.pageTexts.form.name.label')}
                  name={VALIDATION_FIELD_MAPPING.name}
                  value={formData.name}
                  onChange={(e) => handleChange(e, VALIDATION_FIELD_MAPPING.name)}
                  placeholder={t('properties.addEdit.pageTexts.form.name.placeholder')}
                  required
                  disabled={view}
                  error={!!errors[VALIDATION_FIELD_MAPPING.name]}
                  errorMessage={errors[VALIDATION_FIELD_MAPPING.name]}
                />
              </div>
            </div>
             {/* Code */}
            <div className="col-md-6">
              <div className="form-floating form-floating-outline">
                <Input
                  label={t('properties.addEdit.pageTexts.form.code.label')}
                  name={VALIDATION_FIELD_MAPPING.code}
                  value={formData.code}
                  onChange={(e) => handleChange(e, VALIDATION_FIELD_MAPPING.code)}
                  placeholder={t('properties.addEdit.pageTexts.form.code.placeholder')}
                  disabled={view}
                />
              </div>
            </div>
             {/* Property Developer Name */}
            <div className="col-md-6">
              <div className="form-floating form-floating-outline">
                <Input
                  label={t('properties.addEdit.pageTexts.form.propertyDeveloperName.label')}
                  name={VALIDATION_FIELD_MAPPING.property_developer_name}
                  value={formData.property_developer_name}
                  onChange={(e) => handleChange(e, VALIDATION_FIELD_MAPPING.property_developer_name)}
                  placeholder={t('properties.addEdit.pageTexts.form.propertyDeveloperName.placeholder')}
                  disabled={view}
                />
              </div>
            </div>
             {/* Developer (Multi-select) */}
             <div className="col-md-6">
              <div className="form-floating form-floating-outline">
                <Dropdown
                  id={VALIDATION_FIELD_MAPPING.developer_property}
                  label={t('properties.addEdit.pageTexts.form.developer.label')}
                  name={VALIDATION_FIELD_MAPPING.developer_property}
                  values={formData.developer_property || []}
                  options={developerOptions}
                  allOptions={allDeveloperOptions}
                  onChange={(e) => handleChange(e, VALIDATION_FIELD_MAPPING.developer_property)}
                  placeholder={t('properties.addEdit.pageTexts.form.developer.placeholder')}
                  required
                  disabled={view}
                  multiSelect
                  onSearch={(e) => onDropdownSearchChange(e, 'developer')}
                  searchText={dropdownSearchData.developer}
                  error={!!errors[VALIDATION_FIELD_MAPPING.developer_property]}
                  errorMessage={errors[VALIDATION_FIELD_MAPPING.developer_property]}
                />
              </div>
            </div>
             {/* Category */}
             <div className="col-md-6">
              <div className="form-floating form-floating-outline">
                <Dropdown
                  id={VALIDATION_FIELD_MAPPING.categoryId}
                  label={t('properties.addEdit.pageTexts.form.category.label')}
                  name={VALIDATION_FIELD_MAPPING.categoryId}
                  values={formData.category_id ? [String(formData.category_id)] : []}
                  options={categoryOptions}
                  allOptions={allCategoryOptions}
                  onChange={(e) => handleChange(e, 'category_id')}
                  placeholder={t('properties.addEdit.pageTexts.form.category.placeholder')}
                  required
                  disabled={view}
                  onSearch={(e) => onDropdownSearchChange(e, 'category')}
                  searchText={dropdownSearchData.category}
                  error={!!errors[VALIDATION_FIELD_MAPPING.categoryId]}
                  errorMessage={errors[VALIDATION_FIELD_MAPPING.categoryId]}
                />
              </div>
            </div>
            {/* Construction Status */}
             <div className="col-md-6">
              <div className="form-floating form-floating-outline">
                <Dropdown
                  id={VALIDATION_FIELD_MAPPING.construction_status}
                  label={t('properties.addEdit.pageTexts.form.constructionStatus.label')}
                  name={VALIDATION_FIELD_MAPPING.construction_status}
                  values={formData.construction_status ? [String(formData.construction_status)] : []}
                  options={constructionStatusOptions}
                  allOptions={allConstructionStatusOptions}
                  onChange={(e) => handleChange(e, 'construction_status')}
                  placeholder={t('properties.addEdit.pageTexts.form.constructionStatus.placeholder')}
                  required
                  disabled={view}
                  onSearch={(e) => onDropdownSearchChange(e, 'construction_status')}
                  searchText={dropdownSearchData.construction_status}
                  error={!!errors[VALIDATION_FIELD_MAPPING.construction_status]}
                  errorMessage={errors[VALIDATION_FIELD_MAPPING.construction_status]}
                />
              </div>
            </div>
            {/* RERA Number */}
             <div className="col-md-6">
              <div className="form-floating form-floating-outline">
                <Input
                  label={t('properties.addEdit.pageTexts.form.reraNumber.label')}
                  name={VALIDATION_FIELD_MAPPING.rera_number}
                  value={formData.rera_number}
                  onChange={(e) => handleChange(e, VALIDATION_FIELD_MAPPING.rera_number)}
                  placeholder={t('properties.addEdit.pageTexts.form.reraNumber.placeholder')}
                  disabled={view}
                />
              </div>
            </div>
             {/* Amenities (Multi-select) */}
             <div className="col-md-6">
              <div className="form-floating form-floating-outline">
                <Dropdown
                  id={VALIDATION_FIELD_MAPPING.amenities}
                  label={t('properties.addEdit.pageTexts.form.amenities.label')}
                  name={VALIDATION_FIELD_MAPPING.amenities}
                  values={formData.amenities || []}
                  options={amenityOptions}
                  allOptions={allAmenityOptions}
                  onChange={(e) => handleChange(e, VALIDATION_FIELD_MAPPING.amenities)}
                  placeholder={t('properties.addEdit.pageTexts.form.amenities.label')}
                  disabled={view}
                  multiSelect
                  onSearch={(e) => onDropdownSearchChange(e, 'amenities')}
                  searchText={dropdownSearchData.amenities}
                />
              </div>
            </div>

            {/* Launch Date */}
            <div className="col-md-3">
              <div className="form-floating form-floating-outline">
                 <DatePicker
                    label={t('properties.addEdit.pageTexts.form.launchDate.label')}
                    value={parseDateToLocal(formData.launch_date)}
                    onChange={(date: Date | null) => handleChange({ target: { value: date } }, VALIDATION_FIELD_MAPPING.launch_date)}
                    placeholder={t('properties.addEdit.pageTexts.form.launchDate.label')}
                    disabled={view}
                    error={!!errors[VALIDATION_FIELD_MAPPING.launch_date]}
                    helperText={errors[VALIDATION_FIELD_MAPPING.launch_date]}
                 />
              </div>
            </div>
            {/* Possession Date */}
            <div className="col-md-3">
              <div className="form-floating form-floating-outline">
                 <DatePicker
                    label={t('properties.addEdit.pageTexts.form.possessionDate.label')}
                    value={parseDateToLocal(formData.possession_date)}
                    onChange={(date: Date | null) => handleChange({ target: { value: date } }, VALIDATION_FIELD_MAPPING.possession_date)}
                    placeholder={t('properties.addEdit.pageTexts.form.possessionDate.label')}
                    disabled={view}
                 />
              </div>
            </div>
            {/* Approval Status */}
            <div className="col-md-6">
              <div className="form-floating form-floating-outline">
                <Dropdown
                  id={VALIDATION_FIELD_MAPPING.approval_status}
                  label={t('properties.addEdit.pageTexts.form.approvalStatus.label')}
                  name={VALIDATION_FIELD_MAPPING.approval_status}
                  values={formData.approval_status ? [String(formData.approval_status)] : []}
                  options={approvalStatusOptions}
                  allOptions={allApprovalStatusOptions}
                  onChange={(e) => handleChange(e, 'approval_status')}
                  placeholder={t('properties.addEdit.pageTexts.form.approvalStatus.placeholder')}
                  required
                  disabled={view}
                  onSearch={(e) => onDropdownSearchChange(e, 'approval_status')}
                  searchText={dropdownSearchData.approval_status}
                  error={!!errors[VALIDATION_FIELD_MAPPING.approval_status]}
                  errorMessage={errors[VALIDATION_FIELD_MAPPING.approval_status]}
                />
              </div>
            </div>
            {/* Description (Rich Text) */}
            <div className="col-12">
               <div className="form-floating form-floating-outline">
                <RichTextEditor
                   value={formData.description || ''}
                   onChange={(val: string) => handleChange(val, VALIDATION_FIELD_MAPPING.description)}
                   disabled={view}
                   label={t('properties.addEdit.pageTexts.form.description.label')}
                   required
                />
               </div>
               {errors[VALIDATION_FIELD_MAPPING.description] && <div className="text-danger small">{errors[VALIDATION_FIELD_MAPPING.description]}</div>}
            </div>
             {/* WhatsApp Description (Rich Text) */}
             <div className="col-12">
                <div className="form-floating form-floating-outline">
                 <RichTextEditor
                    value={formData.whatsapp_description || ''}
                    onChange={(val: string) => handleChange(val, 'whatsapp_description')}
                    disabled={view}
                    label={t('properties.addEdit.pageTexts.form.otherDetails.whatsAppDescription')}
                    required
                 />
                </div>
                {errors['whatsapp_description'] && <div className="text-danger small">{errors['whatsapp_description']}</div>}
             </div>
          </div>

      <hr className="my-6 mx-n4" />

      {/* 2. Address Details */}
      <h6 className="mb-0">2. {t('properties.addEdit.pageTexts.form.sections.addressDetails')}</h6>
      <div className="row g-6 mt-1">
             <div className="col-md-6">
              <div className="form-floating form-floating-outline">
                <Input
                  label={t('properties.addEdit.pageTexts.form.address1.label')}
                  name={VALIDATION_FIELD_MAPPING.addressLine1}
                  value={formData.address_line_1}
                  onChange={(e) => handleChange(e, 'address_line_1')}
                  placeholder={t('properties.addEdit.pageTexts.form.address1.placeholder')}
                  required
                  disabled={view}
                  error={!!errors[VALIDATION_FIELD_MAPPING.addressLine1]}
                  errorMessage={errors[VALIDATION_FIELD_MAPPING.addressLine1]}
                />
              </div>
             </div>
              <div className="col-md-6">
                <div className="form-floating form-floating-outline">
                  <Input
                    label={t('properties.addEdit.pageTexts.form.address2.label')}
                    name={VALIDATION_FIELD_MAPPING.addressLine2}
                    value={formData.address_line_2}
                    onChange={(e) => handleChange(e, 'address_line_2')}
                    placeholder={t('properties.addEdit.pageTexts.form.address2.placeholder')}
                    disabled={view}
                  />
                </div>
             </div>
             {/* Country */}
              <div className="col-md-4">
                <div className="form-floating form-floating-outline">
                  <Dropdown
                    id={VALIDATION_FIELD_MAPPING.countryId}
                    label={t('properties.addEdit.pageTexts.form.country.label')}
                    name={VALIDATION_FIELD_MAPPING.countryId}
                    values={formData.country_id ? [String(formData.country_id)] : []}
                    options={countryOptions}
                    allOptions={allCountryOptions}
                    onChange={(e) => handleChange(e, 'country_id')}
                    placeholder={t('properties.addEdit.pageTexts.form.country.label')} 
                    required
                    disabled={view}
                    onSearch={(e) => onDropdownSearchChange(e, 'country')}
                    searchText={dropdownSearchData.country}
                    error={!!errors[VALIDATION_FIELD_MAPPING.countryId]}
                    errorMessage={errors[VALIDATION_FIELD_MAPPING.countryId]}
                  />
                </div>
            </div>
            {/* State */}
             <div className="col-md-4">
              <div className="form-floating form-floating-outline">
                <Dropdown
                  id={VALIDATION_FIELD_MAPPING.stateId}
                  label={t('properties.addEdit.pageTexts.form.state.label')}
                  name={VALIDATION_FIELD_MAPPING.stateId}
                  values={formData.state_id ? [String(formData.state_id)] : []}
                  options={stateOptions}
                  allOptions={allStateOptions}
                  onChange={(e) => handleChange(e, 'state_id')}
                  placeholder={t('properties.addEdit.pageTexts.form.state.label')}
                  required
                  disabled={view}
                  onSearch={(e) => onDropdownSearchChange(e, 'state')}
                  searchText={dropdownSearchData.state}
                  error={!!errors[VALIDATION_FIELD_MAPPING.stateId]}
                  errorMessage={errors[VALIDATION_FIELD_MAPPING.stateId]}
                />
              </div>
            </div>
             {/* City */}
             <div className="col-md-4">
              <div className="form-floating form-floating-outline">
                <Dropdown
                  id={VALIDATION_FIELD_MAPPING.cityId}
                  label={t('properties.addEdit.pageTexts.form.city.label')}
                  name={VALIDATION_FIELD_MAPPING.cityId}
                  values={formData.city_id ? [String(formData.city_id)] : []}
                  options={cityOptions}
                  allOptions={allCityOptions}
                  onChange={(e) => handleChange(e, 'city_id')}
                  placeholder={t('properties.addEdit.pageTexts.form.city.label')}
                  required
                  disabled={view}
                  onSearch={(e) => onDropdownSearchChange(e, 'city')}
                  searchText={dropdownSearchData.city}
                  error={!!errors[VALIDATION_FIELD_MAPPING.cityId]}
                  errorMessage={errors[VALIDATION_FIELD_MAPPING.cityId]}
                />
              </div>
            </div>
             {/* Area / AreaId (Single Select) */}
             <div className="col-md-4">
               <div className="form-floating form-floating-outline">
                 <Dropdown
                   id={VALIDATION_FIELD_MAPPING.area_id}
                   label={t('properties.addEdit.pageTexts.form.areaId.label')} 
                   name={VALIDATION_FIELD_MAPPING.area_id}
                   values={formData.area_id ? [String(formData.area_id)] : []}
                   options={areaOptions}
                   allOptions={allAreaOptions}
                   onChange={(e) => handleChange(e, VALIDATION_FIELD_MAPPING.area_id)}
                   placeholder={t('properties.addEdit.pageTexts.form.areaId.placeholder')}
                   required
                   disabled={view}
                   onSearch={(e) => onDropdownSearchChange(e, 'area')}
                   searchText={dropdownSearchData.area}
                   error={!!errors[VALIDATION_FIELD_MAPPING.area_id]}
                   errorMessage={errors[VALIDATION_FIELD_MAPPING.area_id]}
                 />
               </div>
             </div>
             {/* Nearby Areas (Multi-select) */}
             <div className="col-md-4">
               <div className="form-floating form-floating-outline">
                 <Dropdown
                   id={VALIDATION_FIELD_MAPPING.area}
                   label={t('properties.addEdit.pageTexts.form.area.label')}
                   name={VALIDATION_FIELD_MAPPING.area}
                   values={formData.area || []}
                   options={nearbyAreaOptions}
                   allOptions={allNearbyAreaOptions}
                   onChange={(e) => handleChange(e, VALIDATION_FIELD_MAPPING.area)}
                   placeholder={t('properties.addEdit.pageTexts.form.area.label')}
                   required
                   disabled={view}
                   multiSelect
                   onSearch={(e) => onDropdownSearchChange(e, 'nearbyArea')}
                   searchText={dropdownSearchData.nearbyArea}
                   error={!!errors[VALIDATION_FIELD_MAPPING.area]}
                   errorMessage={errors[VALIDATION_FIELD_MAPPING.area]}
                 />
               </div>
             </div>
              {/* Pincode */}
              <div className="col-md-4">
               <div className="form-floating form-floating-outline">
                 <Input
                    label={t('properties.addEdit.pageTexts.form.postal.label')}
                    name={VALIDATION_FIELD_MAPPING.pincode}
                    value={formData.pincode}
                    onChange={(e) => handleChange(e, VALIDATION_FIELD_MAPPING.pincode)}
                    placeholder={t('properties.addEdit.pageTexts.form.postal.placeholder')}
                    required
                    disabled={view}
                    error={!!errors[VALIDATION_FIELD_MAPPING.pincode]}
                    errorMessage={errors[VALIDATION_FIELD_MAPPING.pincode]}
                 />
               </div>
             </div>
             {/* Map Placeholder */}
             <div className="col-12">
                <label className="form-label">{t('properties.addEdit.pageTexts.form.propertyMap.label')}</label>
                <div style={{ height: '350px' }}>
                    <PropertyMap latitude={formData.latitude} longitude={formData.longitude} />
                </div>
                 <div className="row mt-2 g-6">
                      <div className="col-md-6">
                        <div className="form-floating form-floating-outline">
                          <Input
                               label={t('properties.addEdit.pageTexts.form.latitude.label')}
                               name={VALIDATION_FIELD_MAPPING.latitude}
                               value={formData.latitude}
                               onChange={(e) => handleLocalChange(e, VALIDATION_FIELD_MAPPING.latitude)}
                               placeholder={t('properties.addEdit.pageTexts.form.latitude.placeholder')}
                               disabled={view}
                               error={!!errors[VALIDATION_FIELD_MAPPING.latitude]}
                               errorMessage={errors[VALIDATION_FIELD_MAPPING.latitude]}
                           />
                        </div>
                     </div>
                     <div className="col-md-6">
                        <div className="form-floating form-floating-outline">
                          <Input
                               label={t('properties.addEdit.pageTexts.form.longitude.label')}
                               name={VALIDATION_FIELD_MAPPING.longitude}
                               value={formData.longitude}
                               onChange={(e) => handleLocalChange(e, VALIDATION_FIELD_MAPPING.longitude)}
                               placeholder={t('properties.addEdit.pageTexts.form.longitude.placeholder')}
                               disabled={view}
                               error={!!errors[VALIDATION_FIELD_MAPPING.longitude]}
                               errorMessage={errors[VALIDATION_FIELD_MAPPING.longitude]}
                           />
                        </div>
                     </div>
                 </div>
             </div>
          </div>

       <hr className="my-6 mx-n4" />

       {/* 3. Property Contact Details */}
       <h6 className="mb-0">3. {t('properties.addEdit.pageTexts.form.sections.contactDetails')}</h6>
       <div className="row g-6 mt-1">
            <div className="col-12">
                <PropertyContactDetailsTable
                    data={formData.property_contact_details || []}
                    onChange={handlePropertiesContactChange}
                    view={view}
                    errors={errors}
                />
                {errors['property_contact_details'] && <div className="text-danger small">{errors['property_contact_details']}</div>}
            </div>
        </div>

       <hr className="my-6 mx-n4" />

       {/* 4. Inventory Details */}
       <h6 className="mb-0">4. {t('properties.addEdit.pageTexts.form.sections.inventoryDetails')}</h6>
       <div className="row g-6 mt-1">
              <div className="col-md-4">
                  <div className="form-floating form-floating-outline">
                    <Input
                      label={t('properties.addEdit.pageTexts.form.inventoryDetails.totalBlocks')}
                      name={VALIDATION_FIELD_MAPPING.total_blocks}
                      value={formData.total_blocks || ''}
                      onChange={(e) => handleChange(e, VALIDATION_FIELD_MAPPING.total_blocks)}
                      placeholder={t('properties.addEdit.pageTexts.form.inventoryDetails.totalBlocks')}
                      disabled={view}
                      type="number"
                    />
                  </div>
              </div>
              <div className="col-md-4">
                  <div className="form-floating form-floating-outline">
                      <Input
                      label={t('properties.addEdit.pageTexts.form.inventoryDetails.totalFlats')}
                      name={VALIDATION_FIELD_MAPPING.total_flats}
                      value={formData.total_flats || ''}
                      onChange={(e) => handleChange(e, VALIDATION_FIELD_MAPPING.total_flats)}
                      placeholder={t('properties.addEdit.pageTexts.form.inventoryDetails.totalFlats')}
                      disabled={view}
                      type="number"
                    />
                  </div>
              </div>
              <div className="col-md-4">
                  <div className="form-floating form-floating-outline">
                      <Input
                      label={t('properties.addEdit.pageTexts.form.inventoryDetails.totalShops')}
                      name={VALIDATION_FIELD_MAPPING.total_shops}
                      value={formData.total_shops || ''}
                      onChange={(e) => handleChange(e, VALIDATION_FIELD_MAPPING.total_shops)}
                      placeholder={t('properties.addEdit.pageTexts.form.inventoryDetails.totalShops')}
                      disabled={view}
                      type="number"
                    />
                  </div>
              </div>
            <div className="col-12">
                 <InventoryDetailsTable
                    data={formData.property_unit_types || []}
                    onChange={handleInventoryChange}
                    view={view}
                    propertyTypes={propertyTypes}
                    allPropertyTypes={allPropertyTypes}
                    bhkTypes={bhkTypes}
                    allBhkTypes={allBhkTypes}
                    measurements={measurements}
                    allMeasurements={allMeasurements}
                    errors={errors}
                    dropdownSearchData={dropdownSearchData}
                    onDropdownSearchChange={onDropdownSearchChange}
                />
                {errors['property_unit_types'] && <div className="text-danger small">{errors['property_unit_types']}</div>}
            </div>
        </div>


       <hr className="my-6 mx-n4" />

       {/* 5. Other Details */}
       <h6 className="mb-0">5. {t('properties.addEdit.pageTexts.form.sections.otherDetails')}</h6>
       <div className="row g-6 mt-1">
             {/* Notes - col-md-9 */}
             <div className="col-md-9">
              <div className="form-floating form-floating-outline">
                <TextArea
                  id={VALIDATION_FIELD_MAPPING.notes}
                  name="notes"
                  disabled={view}
                  rows={4}
                  label={t('properties.addEdit.pageTexts.form.otherDetails.internalNotes')}
                  placeholder={t('properties.addEdit.pageTexts.form.otherDetails.internalNotes')}
                  value={formData.notes || ''}
                  onChange={(e) => handleChange(e, 'notes')}
                  className="form-control-lg h-100"
                />
              </div>
            </div>



            {/* Status Toggle - col-md-3 */}
            <div className="col-md-3 d-flex align-items-start">
              <div className="form-floating form-floating-outline">
                <Switch
                  name="is_active"
                  label={
                    formData.is_active
                      ? t('properties.addEdit.pageTexts.form.status.activeLabel')
                      : t('properties.addEdit.pageTexts.form.status.inActiveLabel')
                  }
                  checked={formData.is_active}
                  disabled={view}
                  onChange={(e) => handleChange(e, 'is_active')}
                />
              </div>
            </div>

            {/* Featured Toggle - col-md-3 */}
            <div className="col-md-3 d-flex align-items-start">
              <div className="form-floating form-floating-outline">
                <Switch
                  name="is_featured"
                  label={
                    formData.is_featured
                      ? t('properties.addEdit.pageTexts.form.status.featuredLabel')
                      : t('properties.addEdit.pageTexts.form.status.notFeaturedLabel')
                  }
                  checked={formData.is_featured}
                  disabled={view}
                  onChange={(e) => handleChange(e, 'is_featured')}
                />
              </div>
            </div>
        </div>


    </div>
    </div>
  );
};

export default PropertyDetailsTab;
